import React, { memo, useEffect, useCallback, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import Swiper from 'react-id-swiper';
import 'swiper/css/swiper.css';
import ApkButton from '../../components/Button/Button';
import Button from '../../components/Button/Button';
import { logging, logPostInfo } from '../../actions/log';
import { CONTEXT } from '../../config';
import styles from './Banner.module.less';
import { loggingADX, logNative } from '../../utils/utils';
import { setIframeVisible } from '../../actions/layout';
import { useInView } from 'react-intersection-observer';
import { useAliveController } from 'react-activation';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);

  const swiperEl = useRef(null);
  const [banners, setBanners] = useState(null);
  const [swiperParams, setSwiperParams] = useState(null);
  const maxBannerNum = 15;
  const { dropScope } = useAliveController();

  const LOG_ACTION_SOURCE = {
    c_action: `${logInfo.tag}_Banner`,
    c_action_btn: `${logInfo.tag}_Banner_btn`,
    c_action_adx: `${logInfo.tag}_Banner_ADX`,
    p_adx: `${logInfo.tag}_Banner_ADX`,
    source: `${logInfo.tag}_Banner`,
  };
  const [ref, inView] = useInView({
    threshold: 1,
  });

  useEffect(() => {
    if (inView && logInfo && logInfo.changView) {
      let { pageCardContentId } = data && data.dataList && data.dataList[0];
      if (data && data.dataList && data.dataList.length > 1) {
        pageCardContentId = data.dataList.reduce((total, item) => {
          return `${total}${total ? '||' : ''}${item.pageCardContentId}`;
        }, '');
      }
      logInfo.changView({
        pageCardId: pageCardContentId,
        pageCardTitle: data && data.title,
        rankCode: data && data.dataList && data.dataList[0] && data.dataList[0].code,
        cardType: data && data.cardType,
      });
    }
  }, [inView]);
  // 筛洗数据，去除运营配置数据错误情况
  useEffect(() => {
    if (data && data.dataList) {
      const showBanners = [];
      let hasADXBanner = null;
      data.dataList.forEach(banner => {
        if (
          (banner.targetType === '1' && banner.appInfo) ||
          (banner.targetType === '2' && banner.targetUrl) ||
          (banner.targetType === '3' && banner.ztId) ||
          (banner.targetType === '5' && banner.targetContent) ||
          (banner.targetType === '11' && banner.targetContent) ||
          (banner.targetType === '12' && banner.targetContent)
        ) {
          showBanners.push(banner);
        }
        // 是否含有adx数据
        if (banner.targetType === '10' && banner.advertCrid && banner.advertUrl) {
          showBanners.push(banner);
          hasADXBanner = banner;
        }
      });
      setBanners(showBanners.slice(0, maxBannerNum));
      getSwiperParams(showBanners.slice(0, maxBannerNum));
      // 含有adx数据，则打展示的桩
      if (hasADXBanner) {
        loggingADXPV(hasADXBanner);
      }
    }
  }, []);

  // 由于swiper组件没有封装 hover切换事件，所以添加事件：鼠标hover切换轮播图。
  useEffect(() => {
    const swiper = swiperEl.current && swiperEl.current.swiper;
    if (banners && swiper) {
      const paginations = document.querySelectorAll('.swiper-pagination-bullet');
      paginations.forEach((pagination, index) => {
        pagination.addEventListener(
          'mouseenter',
          () => {
            swiper.slideTo(index + banners.length);
          },
          false,
        );
      });
    }
  }, [banners, swiperEl]);

  const loggingADXPV = useCallback(bannerADX => {
    // 推荐页面ADX banner展示打桩
    dispatch(
      logging({
        category: 'I',
        action: LOG_ACTION_SOURCE.p_adx,
        label: '',
        param2: {
          source: LOG_ACTION_SOURCE.source,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          advertCrid: bannerADX.advertCrid,
          advertUrl: bannerADX.advertUrl,
        },
        param3: bannerADX.bizInfo,
      }),
    );
    if (bannerADX && bannerADX.impTracks) {
      const impTracks = JSON.parse(bannerADX.impTracks);
      loggingADX(impTracks);
    }
  }, []);

  const loggingADXClick = useCallback(bannerADX => {
    // 推荐页面ADX banner点击打桩
    dispatch(
      logging({
        category: 'C',
        action: LOG_ACTION_SOURCE.c_action_adx,
        label: '',
        param2: {
          source: LOG_ACTION_SOURCE.source,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          advertCrid: bannerADX.advertCrid,
          advertUrl: bannerADX.advertUrl,
        },
        param3: bannerADX.bizInfo,
      }),
    );
    if (bannerADX && bannerADX.impTracks) {
      const clickTracks = JSON.parse(bannerADX.clickTracks);
      loggingADX(clickTracks);
    }
  }, []);

  const getSwiperParams = bannersArr => {
    const params = {
      speed: 500,
      loop: true,
      slidesPerView: 'auto',
      effect: 'coverflow',
      grabCursor: true,
      centeredSlides: true,
      slideToClickedSlide: true,
      coverflowEffect: {
        rotate: 0,
        stretch: 50,
        depth: 300,
        modifier: 1,
        slideShadows: true,
      },
      autoplay: {
        delay: 3000,
        stopOnLastSlide: false,
        disableOnInteraction: false,
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        renderBullet: (index, className) => {
          return `<div class="${className}">
            <div class="swiper_pagination_round"></div>
          </div>`;
        },
      },
      on: {
        click(e) {
          const targetDom = e.path[0] || {};
          const index = this.activeIndex % bannersArr.length;
          const bannerInfo = bannersArr[index] || {};
          if (targetDom.className && targetDom.className.indexOf(styles.banner_img) > -1) {
            handleBanner(bannerInfo);
          } else if (targetDom.className && targetDom.className.indexOf('soft_btn_text') > -1) {
            handleBannerBtn(bannerInfo);
          }
        },
      },
    };
    setSwiperParams(params);
  };

  const goNext = () => {
    const nowTime = +new Date();
    let diffTime = nowTime - (window.swiperClickTime || 0);
    if (diffTime > 500) {
      diffTime = 500;
    }
    const swiper = swiperEl.current && swiperEl.current.swiper;
    if (swiper !== null) {
      swiper.slideNext(diffTime);
      window.swiperClickTime = nowTime;
    }
  };
  const goPrev = () => {
    const nowTime = +new Date();
    let diffTime = nowTime - (window.swiperClickTime || 0);
    if (diffTime > 500) {
      diffTime = 500;
    }
    const swiper = swiperEl.current && swiperEl.current.swiper;
    if (swiper !== null) {
      swiper.slidePrev(diffTime);
      window.swiperClickTime = nowTime;
    }
  };

  const handleBannerBtn = useCallback(
    bannerInfo => {
      // 推荐页面banner btn点击打桩
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_action_btn,
          label: '',
          param2: {
            source: LOG_ACTION_SOURCE.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
          },
          param3: bannerInfo.bizInfo,
        }),
      );
    },
    [dispatch, logInfoData],
  );

  const handleBanner = useCallback(
    bannerInfo => {
      // 推荐页面banner点击打桩
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_action,
          label: '',
          param2: {
            source: LOG_ACTION_SOURCE.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
          },
          param3: bannerInfo.bizInfo,
        }),
      );

      if (bannerInfo.targetType === '1' || bannerInfo.targetType === '3') {
        dispatch(
          logPostInfo({
            source_p: `${LOG_ACTION_SOURCE.source}||${bannerInfo.pageCardContentId}`,
            source_pp: logInfoData.source_p,
          }),
        );
      }

      setTimeout(() => {
        if (bannerInfo.targetType === '1' && bannerInfo.appInfo && bannerInfo.appInfo.softID) {
          if (bannerInfo.appInfo.androidType === '1') {
            history.push(
              `${CONTEXT}/adetail/${bannerInfo.appInfo.packageName}/${
                bannerInfo.appInfo.versionCode
              }/${bannerInfo.appInfo.bizInfo || '{}'}`,
            );
          } else {
            history.push(
              `${CONTEXT}/detail/soft/${bannerInfo.appInfo.softID || ''}/${bannerInfo.appInfo
                .bizInfo || '{}'}`,
            );
          }
        } else if (bannerInfo.targetType === '2' && bannerInfo.targetUrl) {
          window.open(bannerInfo.targetUrl);
        } else if (bannerInfo.targetType === '3' && bannerInfo.ztId) {
          if (history?.pathname?.indexOf(`${CONTEXT}/special`) < 0) {
            dropScope('special');
          }

          const timer = setTimeout(() => {
            history.push(
              `${CONTEXT}/special?ztId=${bannerInfo.ztId}&bannerId=${bannerInfo.pageCardContentId}`,
            );
            clearTimeout(timer);
          }, 50);
        } else if (bannerInfo.targetType === '5' && bannerInfo.targetContent) {
          let targetContent = {};
          try {
            targetContent = JSON.parse(bannerInfo.targetContent);
          } catch (error) {
            logNative(`分析商城URL JSON解析错误:${error}`, error);
          }
          if (targetContent && targetContent.url) {
            dispatch(
              setIframeVisible({
                visible: true,
                url: targetContent.url,
                source: LOG_ACTION_SOURCE.source,
              }),
            );
          }
        } else if (
          bannerInfo.targetType === '10' &&
          bannerInfo.advertCrid &&
          bannerInfo.advertUrl
        ) {
          loggingADXClick(bannerInfo);
          window.open(bannerInfo.advertUrl);
        } else {
          history.push(`${CONTEXT}/error`);
        }
      }, 30);
    },
    [dispatch, logInfoData, history],
  );

  return banners && banners.length > 0 ? (
    <div className={styles.banner} ref={ref}>
      <Swiper {...swiperParams} ref={swiperEl}>
        {banners.map((item, i) => (
          <div
            key={i}
            onMouseEnter={() => {
              if (swiperEl.current.swiper) swiperEl.current.swiper.autoplay.stop();
            }}
            onMouseLeave={() => {
              if (swiperEl.current.swiper) swiperEl.current.swiper.autoplay.start();
            }}
          >
            <div className={styles.banner_item} key={i}>
              <img className={styles.banner_img} src={item.contentImg} alt="" />
              {item.targetType === '1' && (
                <div className={styles.banner_infos}>
                  {item.appInfo.androidType === '1' ? (
                    <ApkButton
                      soft={item.appInfo}
                      info={{
                        className: styles.banner_soft_button,
                        className_busing: styles.banner_soft_button_busing,
                        className_open: styles.soft_button_open,
                      }}
                      logInfo={{
                        source: `${LOG_ACTION_SOURCE.source}||${item.pageCardContentId}`,
                      }}
                    />
                  ) : (
                    <Button
                      soft={item.appInfo}
                      info={{
                        className: styles.banner_soft_button,
                        className_busing: styles.banner_soft_button_busing,
                        className_open: styles.soft_button_open,
                      }}
                      logInfo={{
                        source: `${LOG_ACTION_SOURCE.source}||${item.pageCardContentId}`,
                      }}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="swiper-slide-shadow-left" />
            <div className="swiper-slide-shadow-right" />
          </div>
        ))}
      </Swiper>
      <div className={styles.banner_swiper_prev} onClick={goPrev} />
      <div className={styles.banner_swiper_next} onClick={goNext} />
    </div>
  ) : (
    ''
  );
});
